'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useSWRConfig } from 'swr';
import {
  Key,
  Info,
  Loader2,
  Copy,
  Check,
  ExternalLink,
  ShieldCheck,
  AlertCircle,
  Building,
  Phone,
  Eye,
  EyeOff,
  Server,
  Lock,
} from 'lucide-react';

interface AddInstanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddInstanceModal({
  open,
  onOpenChange,
  onSuccess,
}: AddInstanceModalProps) {
  const { mutate } = useSWRConfig();

  // Form Fields
  const [instanceName, setInstanceName] = useState('');
  const [metaPhoneNumberId, setMetaPhoneNumberId] = useState('');
  const [metaBusinessId, setMetaBusinessId] = useState('');
  const [metaToken, setMetaToken] = useState('');
  const [showToken, setShowToken] = useState(false);

  // Statuses
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Copy States
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const webhookUrl =
    process.env.NEXT_PUBLIC_EVOLUTION_WEBHOOK_URL ||
    process.env.NEXT_PUBLIC_WEBHOOK_URL ||
    'https://zeric.link/api/webhook';
  const webhookToken =
    process.env.NEXT_PUBLIC_EVOLUTION_WEBHOOK_TOKEN || 'global_webhook_token_4a9b2c';

  const copyToClipboard = (text: string, type: 'webhook' | 'token') => {
    navigator.clipboard.writeText(text);
    if (type === 'webhook') {
      setCopiedWebhook(true);
      setTimeout(() => setCopiedWebhook(false), 2000);
    } else {
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
    toast.success('Copied to clipboard');
  };

  // Submit and create instance directly without validation
  const handleCloudApiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!instanceName.trim()) {
      setError('Instance name is required.');
      return;
    }
    if (!metaPhoneNumberId.trim() || !metaBusinessId.trim() || !metaToken.trim()) {
      setError('Please fill in Phone Number ID, WhatsApp Business Account ID, and System User Token.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const res = await fetch('/api/instance/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instanceName: instanceName.trim(),
          integration: 'WHATSAPP-BUSINESS',
          metaToken: metaToken.trim(),
          metaBusinessId: metaBusinessId.trim(),
          metaPhoneNumberId: metaPhoneNumberId.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to connect WhatsApp Cloud API instance.');
      }

      toast.success('WhatsApp Cloud API connected successfully!');
      await mutate('/api/team');
      await mutate('/api/instance/list');
      await mutate('/api/instance/details');
      if (onSuccess) onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setInstanceName('');
    setMetaPhoneNumberId('');
    setMetaBusinessId('');
    setMetaToken('');
    setError(null);
    setShowToken(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-[92vw] md:w-[740px] max-w-[740px] max-h-[88vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 rounded-2xl border border-border/80 shadow-2xl bg-card">
        
        {/* Header: Title, Description, and Badges */}
        <DialogHeader className="space-y-1 text-left border-b border-border/50 pb-3">
          <div className="flex flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="size-9 sm:size-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <Key className="size-4 sm:size-5" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base sm:text-lg font-bold tracking-tight text-foreground truncate">
                  Connect WhatsApp Cloud API
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground truncate">
                  Official Meta WhatsApp Business Cloud API Integration
                </DialogDescription>
              </div>
            </div>

            <Badge className="bg-emerald-600/10 hover:bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-[11px] font-semibold px-2.5 py-0.5 rounded-full gap-1 shrink-0 hidden sm:flex">
              <ShieldCheck className="size-3" />
              Official Meta Platform
            </Badge>
          </div>
        </DialogHeader>

        <form onSubmit={handleCloudApiSubmit} className="space-y-3.5 pt-1">
          
          {/* Form Fields: 2-Column Responsive Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3.5">
            
            {/* 1. Instance Name */}
            <div className="space-y-1 min-w-0">
              <div className="flex items-center justify-between">
                <Label htmlFor="inst-name" className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Building className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                  Instance Name <span className="text-destructive">*</span>
                </Label>
                <span className="text-[11px] text-muted-foreground">Workspace label</span>
              </div>
              <Input
                id="inst-name"
                placeholder="e.g. Sales Team or Primary Support"
                value={instanceName}
                onChange={(e) => {
                  setInstanceName(e.target.value);
                  setError(null);
                }}
                className="rounded-lg h-9.5 text-xs sm:text-sm bg-muted/20 focus-visible:ring-emerald-500/20 focus-visible:border-emerald-500"
                disabled={isSubmitting}
                required
              />
            </div>

            {/* 2. Phone Number ID */}
            <div className="space-y-1 min-w-0">
              <div className="flex items-center justify-between">
                <Label htmlFor="phone-id" className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Phone className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                  Phone Number ID <span className="text-destructive">*</span>
                </Label>
                <span className="text-[11px] text-muted-foreground">Meta Setup tab</span>
              </div>
              <Input
                id="phone-id"
                placeholder="e.g. 104829384910293"
                value={metaPhoneNumberId}
                onChange={(e) => {
                  setMetaPhoneNumberId(e.target.value);
                  setError(null);
                }}
                className="rounded-lg h-9.5 text-xs sm:text-sm font-mono bg-muted/20 focus-visible:ring-emerald-500/20 focus-visible:border-emerald-500"
                disabled={isSubmitting}
                required
              />
            </div>

            {/* 3. WABA Account ID */}
            <div className="space-y-1 min-w-0">
              <div className="flex items-center justify-between">
                <Label htmlFor="waba-id" className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Key className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                  WhatsApp Business Account ID <span className="text-destructive">*</span>
                </Label>
                <span className="text-[11px] text-muted-foreground">WABA ID</span>
              </div>
              <Input
                id="waba-id"
                placeholder="e.g. 104829384910294"
                value={metaBusinessId}
                onChange={(e) => {
                  setMetaBusinessId(e.target.value);
                  setError(null);
                }}
                className="rounded-lg h-9.5 text-xs sm:text-sm font-mono bg-muted/20 focus-visible:ring-emerald-500/20 focus-visible:border-emerald-500"
                disabled={isSubmitting}
                required
              />
            </div>

            {/* 4. System User Permanent Token */}
            <div className="space-y-1 min-w-0">
              <div className="flex items-center justify-between">
                <Label htmlFor="system-token" className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Lock className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                  System User Token <span className="text-destructive">*</span>
                </Label>
                <a
                  href="https://developers.facebook.com/docs/whatsapp/business-management-api/get-started"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-0.5 font-medium"
                >
                  Meta docs <ExternalLink className="size-2.5" />
                </a>
              </div>
              <div className="relative flex items-center">
                <Input
                  id="system-token"
                  type={showToken ? 'text' : 'password'}
                  placeholder="EAAG..."
                  value={metaToken}
                  onChange={(e) => {
                    setMetaToken(e.target.value);
                    setError(null);
                  }}
                  className="rounded-lg h-9.5 text-xs sm:text-sm font-mono pr-9 bg-muted/20 focus-visible:ring-emerald-500/20 focus-visible:border-emerald-500"
                  disabled={isSubmitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-2.5 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showToken ? 'Hide token' : 'Show token'}
                >
                  {showToken ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                </button>
              </div>
            </div>

          </div>

          {/* Webhook Configuration Strip (Compact, 2-Column Responsive) */}
          <div className="p-3 rounded-xl bg-muted/30 border border-border/70 space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-foreground">
              <div className="flex items-center gap-1.5 min-w-0">
                <Info className="size-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <span className="truncate">Meta Webhook Configuration</span>
              </div>
              <span className="text-[10px] text-muted-foreground font-normal hidden sm:inline">
                Meta Developer Portal &gt; WhatsApp &gt; Configuration
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="space-y-0.5 min-w-0">
                <span className="text-[10px] text-muted-foreground font-medium">Callback URL:</span>
                <div className="flex items-center justify-between gap-1.5 bg-background p-1.5 px-2 rounded-lg border border-border/50 font-mono text-[11px] min-w-0">
                  <span className="truncate text-muted-foreground select-all min-w-0 flex-1">{webhookUrl}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={() => copyToClipboard(webhookUrl, 'webhook')}
                  >
                    {copiedWebhook ? <Check className="size-3 text-emerald-600" /> : <Copy className="size-3" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-0.5 min-w-0">
                <span className="text-[10px] text-muted-foreground font-medium">Verify Token:</span>
                <div className="flex items-center justify-between gap-1.5 bg-background p-1.5 px-2 rounded-lg border border-border/50 font-mono text-[11px] min-w-0">
                  <span className="truncate text-muted-foreground select-all min-w-0 flex-1">{webhookToken}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={() => copyToClipboard(webhookToken, 'token')}
                  >
                    {copiedToken ? <Check className="size-3 text-emerald-600" /> : <Copy className="size-3" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-start gap-2 animate-in fade-in duration-150">
              <AlertCircle className="size-4 shrink-0 mt-0.5" />
              <span className="leading-relaxed">{error}</span>
            </div>
          )}

          {/* Action Bar */}
          <div className="flex items-center gap-2 justify-end pt-2.5 border-t border-border/60">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="flex-1 sm:flex-none h-9.5 text-xs px-3"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 text-white h-9.5 px-4 text-xs sm:text-sm font-semibold gap-1.5 shadow-sm"
            >
              {isSubmitting ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Key className="size-3.5" />
              )}
              <span>{isSubmitting ? 'Connecting...' : 'Connect WhatsApp Cloud API'}</span>
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddInstanceModal;
